import { JSX, lazy } from "react";

import Home from "../pages/Home/Home";
import Login from "../pages/Login/Login";
import NotFound from "../pages/NotFound/NotFound";
import { ModalConfig } from "../components/Contexts/GlobalModalContext";
import { RoleConfig } from "../DTOs/Settings/RoleConfigDto";

const Analytes = lazy(() => import("../pages/Admin/References/Analytes/Analytes"));
const BlastMaterial = lazy(() => import("../pages/Admin/References/Blasts/BlastMaterial"));
const BlastMaterialType = lazy(() => import("../pages/Admin/References/Blasts/BlastMaterialType"));
const BlastPattern = lazy(() => import("../pages/Admin/References/Blasts/BlastPattern/BlastPattern"));
const BucketFactors = lazy(() => import("../pages/Admin/References/BucketFactors/BucketFactors"));
const BucketTypes = lazy(() => import("../pages/Admin/References/BucketTypes/BucketTypes"));
const Densities = lazy(() => import("../pages/Admin/References/Densities/Densities"));
const DiggingLoadingSummaryPage = lazy(() => import("../pages/ChatreeProduction/DiggingLoadingSummaryPage/DiggingLoadingSummaryPage"));
const Production = lazy(() => import("../pages/ChatreeProduction/Production"));
const BlastHoleDrilling = lazy(() => import("../pages/Drilling/BlastHoleDrilling/BlastHoleDrilling"));
const BlastHoleDrillingList = lazy(() => import("../pages/Drilling/BlastHoleDrilling/BlastHoleDrillingList"));
const GeologyCrushing = lazy(() => import("../pages/GeologyCrushing/GeologyCrushing"));
const GeologyMilling = lazy(() => import("../pages/GeologyMilling/GeologyMilling"));
const GeologyOreBlocks = lazy(() => import("../pages/GeologyOreBlocks/GeologyOreBlocks"));
const GeologyOreTally = lazy(() => import("../pages/GeologyOreTally/GeologyOreTally"));
const GeologyRehandle = lazy(() => import("../pages/GeologyRehandle/GeologyRehandle"));
const Operations = lazy(() => import("../pages/Operations/Operations"));
const RoyaltyFees = lazy(() => import("../pages/RoyaltyFees/RoyaltyFees"));
const StockpileBalances = lazy(() => import("../pages/StockpileBalances/StockpileBalances"));
const Stoppages = lazy(() => import("../pages/Stoppages/Stoppages"));
const Departments = lazy(() => import("../pages/Admin/References/Departments/Departments"));
const MaterialTypes = lazy(() => import("../pages/Admin/References/MaterialTypes/MaterialTypes"));
const Plants = lazy(() => import("../pages/Admin/References/Plants/Plants"));
const DowntimeSubtypes = lazy(() => import("../pages/Admin/References/DowntimeSubTypes/DownTimeSubTypes"));
const PitStages = lazy(() => import("../pages/Admin/References/PitStages/PitStages"));
const Targets = lazy(() => import("../pages/Admin/References/Targets/Targets"));
const MinePeriods = lazy(() => import("../pages/Admin/References/MinePeriods/MinePeriods"));
const TargetTypes = lazy(() => import("../pages/Admin/References/TargetTypes/TargetTypes"));
const DailyReport = lazy(() => import("../pages/Reports/DailyReport/DailyReport"));
const GeologyDrilling = lazy(() => import("../pages/GeologyDrilling/GeologyDrilling"));
const Users = lazy(() => import("../pages/Admin/References/Users/Users"));
const MaterialTypeClasses = lazy(() => import("../pages/Admin/References/MaterialTypeClasses/MaterialTypeClasses"));
const PlantTypes = lazy(() => import("../pages/Admin/References/PlantTypes/PlantTypes"));
const Locations = lazy(() => import("../pages/Admin/References/Locations/Locations"));
const Dewatering = lazy(() => import("../pages/Dewatering/Dewatering"));
const GeologyRecalcModal = lazy(() => import("../pages/GeologyRecalcModal/GeologyRecalcModal").then(m => ({ default: m.GeologyRecalcModal })));
const MorningReport = lazy(() => import("../pages/Reports/MorningReport/MorningReport"));
const AccountPage = lazy(() => import("../pages/Account/Account"));
const ApiKeys = lazy(() => import("../pages/Account/ApiKeys/ApiKeys"));
const RoyaltyAreaBalances = lazy(() => import("../pages/RoyaltyAreaBalances/RoyaltyAreaBalances").then(m => ({ default: m.RoyaltyAreaBalances })));
const GeologyProduction = lazy(() => import("../pages/GeologyProduction/GeologyProduction/GeologyProduction"));
const GeoMiningMvtSummaryList = lazy(() => import("../pages/GeologyProduction/GeoMiningMvtSummaryList/GeoMiningMvtSummaryList"));
const TargetImportList = lazy(() => import("../pages/Targets/Import/TargetImportList"));
const TargetImportTemplateEditor = lazy(() => import("../pages/Targets/Import/TargetImportTemplateEditor"));
const TargetImportRun = lazy(() => import("../pages/Targets/Import/TargetImportRun"));
const TargetImportAnalyze = lazy(() => import("../pages/Targets/Import/TargetImportAnalyze"));
const ReportLibraryPage = lazy(() => import("../pages/Reports/ReportLibraryPage/ReportLibraryPage"));
const ReportBuilderPage = lazy(() => import("../pages/Reports/ReportBuilderPage/ReportBuilderPage"));
const ReportViewPage = lazy(() => import("../pages/Reports/ReportViewPage/ReportViewPage"));
const DataConnectionsPage = lazy(() => import("../pages/Reports/DataConnectionsPage/DataConnectionsPage"));

type RouteShell = { path: string; isShiftRelatedPage?: boolean; children?: RouteShell[] };

// Note: When adding a new shift-related route to createAppRoutes, add it here too.
const ROUTE_SHELLS: readonly RouteShell[] = [
  { path: "operations", isShiftRelatedPage: true },
  {
    path: "production", children: [
      { path: "show", isShiftRelatedPage: true },
      { path: "rehandle", isShiftRelatedPage: true },
      { path: "crushing", isShiftRelatedPage: true },
      { path: "ore-tally", isShiftRelatedPage: true },
    ]
  },
  { path: "stoppages", isShiftRelatedPage: true },
  {
    path: "db", children: [
      { path: "bh-drilling", isShiftRelatedPage: true },
      { path: "dewatering", isShiftRelatedPage: true },
    ]
  },
  {
    path: "geology", children: [
      { path: "production", isShiftRelatedPage: true },
      { path: "ore-tally", isShiftRelatedPage: true },
      { path: "milling", isShiftRelatedPage: true },
      { path: "drilling", isShiftRelatedPage: true },
    ]
  },
];

function collectShiftRelatedPaths(shells: readonly RouteShell[], parent = ""): string[] {
  return shells.flatMap(({ path, isShiftRelatedPage, children }) => {
    const full = `${parent}/${path}`;
    return [
      ...(isShiftRelatedPage ? [full] : []),
      ...(children ? collectShiftRelatedPaths(children, full) : []),
    ];
  });
}

export const SHIFT_RELATED_PAGE_PATHS: ReadonlySet<string> = new Set(collectShiftRelatedPaths(ROUTE_SHELLS));

export interface AppRouteConfig {
  path: string;
  label?: string;
  ignore?: boolean; // Note: If true, this route will be ignored in the menu generation
  element?: JSX.Element;
  action?: (showModal: (config: ModalConfig) => void) => void; // For routes that trigger an action instead of navigation
  protected?: boolean;
  access?: string[]; // Note: OR logic — user needs at least one of these
  accessAll?: string[]; // Note: AND logic — user needs all of these
  children?: AppRouteConfig[];
}

export function createAppRoutes({ privilegeRoles, modules }: RoleConfig): AppRouteConfig[] {
  const { user, admin } = privilegeRoles;
  const { mining, geology, maintenance, lotusHall, account } = modules;

  const perms = {
    ADMIN: [admin],
    MINING: [mining],
    GEOLOGY: [geology],
    MINING_LOTUS: [mining, lotusHall],
    MINING_GEO: [mining, geology],
    MINING_MAIN: [mining, maintenance],
    MINING_LOTUS_MAIN: [mining, lotusHall, maintenance],
    MINING_LOTUS_GEO: [mining, lotusHall, geology],
    MINING_GEO_MAIN: [mining, geology, maintenance],
    MINING_GEO_MAIN_ADMIN: [mining, geology, maintenance, admin],
    EVERYONE: [account, mining, geology, maintenance, lotusHall, admin, user],
  };

  return [
    {
      path: "/login",
      element: <Login />,
      protected: false,
      ignore: true
    },
    {
      path: "/",
      element: <Home />,
      protected: true,
      label: "Home"
    },
    {
      path: "/account",
      element: <AccountPage />,
      protected: true,
      ignore: true,
      label: "Account"
    },
    {
      path: "/api-keys",
      element: <ApiKeys />,
      protected: true,
      ignore: true,
      label: "API Keys",
      access: perms.ADMIN
    },
    {
      path: "operations", element: <Operations />, protected: true,
      access: perms.MINING_LOTUS, label: "Operations List"
    },
    {
      path: "production", protected: true,
      access: perms.MINING_LOTUS_GEO,
      children: [
        {
          path: "show",
          element: <Production />,
          protected: true,
          access: perms.MINING_LOTUS,
          label: "Add/Edit Production"
        },
        {
          path: "list",
          element: <DiggingLoadingSummaryPage />,
          protected: true,
          access: perms.MINING_LOTUS,
          label: "Production List"
        },
        {
          path: "rehandle", element: <GeologyRehandle />, protected: true,
          access: perms.MINING_LOTUS_GEO, label: "Rehandle"
        },
        {
          path: "crushing", element: <GeologyCrushing />, protected: true,
          access: perms.MINING_LOTUS_GEO, label: "Crushing"
        },
        {
          path: "ore-tally", element: <GeologyOreTally />, protected: true,
          access: perms.MINING_LOTUS, label: "Ore Tally"
        },
      ]
    },
    {
      path: "stoppages", element: <Stoppages />, protected: true,
      access: perms.MINING_LOTUS_MAIN
    },
    {
      path: "db", protected: true,
      access: perms.MINING_LOTUS, label: "Drill & Blast",
      children: [
        {
          path: "bh-drilling", element: <BlastHoleDrilling />, protected: true,
          access: perms.MINING_LOTUS, label: "Add/Edit Blast Hole Drilling"
        },
        {
          path: "bh-drilling-list", element: <BlastHoleDrillingList />, protected: true,
          access: perms.MINING_LOTUS, label: "Blast Hole Drilling List"
        },
        {
          path: "blasts",
          access: perms.MINING_LOTUS,
          protected: true,
          children: [
            {
              path: "pattern", element: <BlastPattern />, protected: true,
              access: perms.MINING_LOTUS, label: "Patterns"
            },
            {
              path: "material", element: <BlastMaterial />, protected: true,
              access: perms.MINING_LOTUS, label: "Materials"
            },
            {
              path: "material-type", element: <BlastMaterialType />, protected: true,
              access: perms.MINING_LOTUS, label: "Material Types"
            }
          ]
        },
        {
          path: "dewatering", element: <Dewatering />, protected: true,
          access: perms.MINING
        },
      ]
    },
    {
      path: "geology", protected: true,
      access: perms.GEOLOGY,
      children: [
        {
          path: "unused", protected: true,
          action: (showModal) => showModal({ content: () => <GeologyRecalcModal /> }),
          label: "Recalculate Stockpiles", accessAll: [admin, geology]
        },
        {
          path: "production", element: <GeologyProduction />, protected: true,
          access: perms.GEOLOGY, label: "Show Mining Mvt"
        },
        {
          path: "mining-movements", element: <GeoMiningMvtSummaryList />, protected: true,
          access: perms.GEOLOGY, label: "Mining Mvt List"
        },
        {
          path: "stockpile-balances", element: <StockpileBalances />, protected: true,
          access: perms.GEOLOGY, label: "Stockpile Balances"
        },
        {
          path: "ore-blocks", element: <GeologyOreBlocks />, protected: true,
          access: perms.GEOLOGY, label: "Ore Blocks"
        },
        {
          path: "ore-tally", element: <GeologyOreTally />, protected: true,
          access: perms.GEOLOGY, label: "Ore Tally"
        },
        {
          path: "milling", element: <GeologyMilling />, protected: true,
          access: perms.GEOLOGY
        },
        {
          path: "drilling", element: <GeologyDrilling />, protected: true,
          access: perms.GEOLOGY
        },
      ]
    },
    {
      path: "reports",
      protected: true, access: perms.MINING,
      children: [
        {
          path: "daily", element: <DailyReport />, protected: true,
          label: "Daily Report", access: perms.MINING,
        },
        {
          path: "morning", element: <MorningReport />, protected: true,
          label: "Morning Meeting Report", access: perms.MINING,
        },
        {
          path: "library", element: <ReportLibraryPage />, protected: true,
          label: "Report Library", access: perms.MINING,
        },
        {
          path: "builder", element: <ReportBuilderPage />, protected: true,
          label: "Eclipse Report Builder", accessAll: [admin, mining],
        },
        {
          path: "view/:id", element: <ReportViewPage />, protected: true,
          ignore: true, access: perms.MINING,
        },
        {
          path: "data-connections", element: <DataConnectionsPage />, protected: true,
          label: "Data Connections", access: perms.MINING,
        }
      ]
    },
    {
      path: "royalty-fees", element: <RoyaltyFees />, protected: true,
      access: perms.MINING, label: "Royalty Fees"
    },
    {
      path: "royalty-area-balances", element: <RoyaltyAreaBalances />, protected: true,
      access: perms.MINING, label: "Royalty Area Balances"
    },
    {
      path: "targets",
      protected: true,
      access: perms.MINING_GEO_MAIN_ADMIN,
      children: [
        {
          path: "imports",
          element: <TargetImportList />,
          protected: true,
          access: perms.MINING_GEO_MAIN_ADMIN,
          label: "Target Imports",
        },
        {
          path: "imports/analyze",
          element: <TargetImportAnalyze />,
          protected: true,
          ignore: true,
          access: perms.MINING_GEO_MAIN_ADMIN,
        },
        {
          path: "imports/templates/new",
          element: <TargetImportTemplateEditor />,
          protected: true,
          ignore: true,
          access: perms.MINING_GEO_MAIN_ADMIN,
        },
        {
          path: "imports/templates/:id",
          element: <TargetImportTemplateEditor />,
          protected: true,
          ignore: true,
          access: perms.MINING_GEO_MAIN_ADMIN,
        },
        {
          path: "imports/run/:templateId",
          element: <TargetImportRun />,
          protected: true,
          ignore: true,
          access: perms.MINING_GEO_MAIN_ADMIN,
        },
      ]
    },
    {
      path: "admin",
      protected: true,
      access: perms.MINING_GEO_MAIN_ADMIN,
      children: [
        {
          path: "users", element: <Users />, protected: true,
          access: perms.ADMIN
        },
        {
          path: "references",
          protected: true,
          access: perms.MINING_GEO_MAIN,
          children: [
            {
              path: "analytes", element: <Analytes />, protected: true,
              access: perms.MINING_GEO
            },
            {
              path: "bucket-factors", element: <BucketFactors />, protected: true,
              access: perms.MINING_GEO
            },
            {
              path: "bucket-types", element: <BucketTypes />, protected: true,
              access: perms.MINING
            },
            {
              path: "densities", element: <Densities />, protected: true,
              access: perms.MINING_GEO
            },
            {
              path: "departments", element: <Departments />, protected: true,
              access: perms.MINING
            },
            {
              path: "downtime-subtypes", element: <DowntimeSubtypes />, protected: true,
              access: perms.MINING
            },
            {
              path: "material-types", element: <MaterialTypes />, protected: true,
              access: perms.MINING_GEO
            },
            {
              path: "material-type-classes", element: <MaterialTypeClasses />, protected: true,
              access: perms.MINING_GEO
            },
            {
              path: "plant", element: <Plants />, protected: true,
              access: perms.MINING_MAIN
            },
            {
              path: "plant-types", element: <PlantTypes />, protected: true,
              access: perms.MINING_MAIN
            },
            {
              path: "pit-stages", element: <PitStages />, protected: true, label: "Pit stages",
              access: perms.MINING
            },
            {
              path: "locations", element: <Locations />, protected: true,
              access: perms.MINING_GEO
            },
            {
              path: "targets", element: <Targets />, protected: true, label: "Targets and EOM Actuals",
              access: perms.MINING
            },
            {
              path: "target-types", element: <TargetTypes />, protected: true, label: "Target Types",
              access: perms.ADMIN
            },
            {
              path: "mine-periods", element: <MinePeriods />, protected: true, label: "Mine Periods",
              access: perms.ADMIN
            }
          ]
        }
      ]
    },
    {
      path: "*",
      element: <NotFound />,
      protected: true
    }
  ];
}
